import React, { useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { apiService } from "../../api/services/apiService";
import type { ProductManagementGroup } from "../../types";
import PmgAssetAssignmentPicker from "./PmgAssetAssignmentPicker";

interface PmgDetailDialogProps {
  group: ProductManagementGroup | null;
  open: boolean;
  canManage: boolean;
  memberEmails: string[];
  onClose: () => void;
  onRefresh: (uuid: string) => Promise<void>;
  onError: (message: string) => void;
}

const PmgDetailDialog: React.FC<PmgDetailDialogProps> = ({
  group,
  open,
  canManage,
  memberEmails,
  onClose,
  onRefresh,
  onError,
}) => {
  const [saving, setSaving] = useState(false);
  const [newOwnerEmail, setNewOwnerEmail] = useState("");
  const [newMemberEmail, setNewMemberEmail] = useState("");

  const handleAddOwner = async () => {
    if (!group || !newOwnerEmail.trim()) return;
    setSaving(true);
    try {
      await apiService.addPmgOwner(group.uuid, newOwnerEmail.trim());
      setNewOwnerEmail("");
      await onRefresh(group.uuid);
    } catch (err: unknown) {
      onError(err instanceof Error ? err.message : "Failed to add owner");
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveOwner = async (email: string) => {
    if (!group) return;
    setSaving(true);
    try {
      await apiService.removePmgOwner(group.uuid, email);
      await onRefresh(group.uuid);
    } catch (err: unknown) {
      onError(err instanceof Error ? err.message : "Failed to remove owner");
    } finally {
      setSaving(false);
    }
  };

  const handleAddMember = async () => {
    if (!group || !newMemberEmail.trim()) return;
    setSaving(true);
    try {
      await apiService.addPmgMember(group.uuid, newMemberEmail.trim());
      setNewMemberEmail("");
      await onRefresh(group.uuid);
    } catch (err: unknown) {
      onError(err instanceof Error ? err.message : "Failed to add member");
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveMember = async (email: string) => {
    if (!group) return;
    setSaving(true);
    try {
      await apiService.removePmgMember(group.uuid, email);
      await onRefresh(group.uuid);
    } catch (err: unknown) {
      onError(err instanceof Error ? err.message : "Failed to remove member");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {group?.name ?? "Group"}
        {!canManage && group && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            View only — contact a group owner to change membership or assignments.
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {group && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Owners
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
                {group.owners.map((email) => (
                  <Chip
                    key={email}
                    label={email}
                    size="small"
                    onDelete={
                      canManage ? () => void handleRemoveOwner(email) : undefined
                    }
                    disabled={saving}
                  />
                ))}
              </Box>
              {canManage && (
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Autocomplete
                    freeSolo
                    options={memberEmails}
                    inputValue={newOwnerEmail}
                    onInputChange={(_e, value) => setNewOwnerEmail(value)}
                    renderInput={(params) => (
                      <TextField {...params} size="small" label="Owner email" fullWidth />
                    )}
                    sx={{ flex: 1 }}
                  />
                  <Button variant="outlined" onClick={() => void handleAddOwner()} disabled={saving}>
                    Add
                  </Button>
                </Box>
              )}
            </Box>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Members
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
                {group.members.map((email) => (
                  <Chip
                    key={email}
                    label={email}
                    size="small"
                    onDelete={
                      canManage ? () => void handleRemoveMember(email) : undefined
                    }
                    disabled={saving}
                  />
                ))}
              </Box>
              {canManage && (
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Autocomplete
                    freeSolo
                    options={memberEmails}
                    inputValue={newMemberEmail}
                    onInputChange={(_e, value) => setNewMemberEmail(value)}
                    renderInput={(params) => (
                      <TextField {...params} size="small" label="Member email" fullWidth />
                    )}
                    sx={{ flex: 1 }}
                  />
                  <Button variant="outlined" onClick={() => void handleAddMember()} disabled={saving}>
                    Add
                  </Button>
                </Box>
              )}
            </Box>

            {canManage ? (
              <PmgAssetAssignmentPicker
                pmgUuid={group.uuid}
                onChanged={() => void onRefresh(group.uuid)}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                {group.assignedAssetCount} asset(s) assigned to this group.
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PmgDetailDialog;
